import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import InboxEditorModal from '../components/admin/inbox/settings/InboxEditorModal';
import {
    useInboxes,
    useInboxEmployeesForInbox,
    useInboxMembers,
    useInboxMailboxes,
    useUpdateInbox,
    useAttachMailbox,
    useDetachMailbox,
    useMailboxAccessRequests,
    useMailboxTokens,
    useRequestMailboxAccess,
    useResendMailboxAccess,
    useRevokeMailboxAccess,
    useCancelMailboxAccess,
} from '../hooks/useUnifiedInbox';
import { isOrgAdminUser } from '../utils/orgRoles';
import { providerForMailbox } from '../utils/mailboxProvider';
import { connectionsFromRefs } from '../utils/inboxMailboxes';

/**
 * Issue #662 — the per-inbox settings surface a MANAGER can reach.
 *
 * Rendered identically by (a) the "Manage mailboxes" action in the mailbox
 * filter (pages/Inbox.jsx owns that modal) and (b) the deep-linkable
 * /inbox/:inboxId/settings route (App.js InboxManagerRoute). No AdminLayout —
 * the route must not pull admin chrome.
 *
 * The modal uses the same six-step InboxEditorModal configuration as Admin →
 * Unified Inbox Settings. The API still enforces the manager grant boundary:
 * managers can edit their granted inbox, while admin-only destructive/request
 * lifecycle actions remain hidden unless the current user is an org admin.
 * The route and modal therefore share one configuration source of truth without
 * granting managers access to the org-wide admin list or recovery/delete flows.
 *
 * Data sources (all manager-legal):
 *  - useInboxes()            — GET /inboxes is grant-scoped server-side.
 *  - useInboxMembers()       — GET /inboxes/{id}/members is viewer-gated and
 *                              the single non-admin source of member names.
 *  - useInboxMailboxes()     — GET /inboxes/{id}/mailboxes (responder-gated).
 *  - inbox-scoped access requests are manager-readable and manager-requestable;
 *    resend/cancel/revoke remain org-admin-only in the backend and are hidden
 *    here for managers.
 *  - PATCH /inboxes/{id} and attach/detach are manager-or-admin gated; the
 *    server guardrails (422 inbox_requires_a_manager /
 *    cannot_drop_own_manager_grant / cannot_revoke_admin_grant) back the
 *    client up.
 */
export default function InboxSettingsPage({ inboxId: inboxIdProp, onClose }) {
    const { user } = useAuth();
    const isAdmin = isOrgAdminUser(user);

    // The page is mounted two ways (see header docs): the mailbox-filter
    // modal passes `inboxId` as a prop (Inbox.jsx), while the deep-linkable
    // /inbox/:inboxId/settings route renders it bare (App.js InboxManagerRoute)
    // — there the id can only come from the router params. Reading the prop
    // first keeps both call sites on one component; without the useParams
    // fallback the route always saw `undefined` and rendered its not-found
    // state (QA F-001).
    const params = useParams();
    const inboxId = inboxIdProp || params?.inboxId;
    const { data: inboxes = [], isLoading: inboxesLoading } = useInboxes();
    const inbox = useMemo(
        () => inboxes.find(i => i.inbox_id === inboxId),
        [inboxes, inboxId]
    );

    const membersQuery = useInboxMembers(inboxId, { pageSize: 100 });

    // Attached mailboxes (attach/detach scope).
    const { data: mailboxData } = useInboxMailboxes(inboxId);
    const connections = useMemo(
        () => connectionsFromRefs(mailboxData),
        [mailboxData]
    );


    const { data: mailboxTokens = [] } = useMailboxTokens();
    const { data: microsoftAccessRequests = [] } = useMailboxAccessRequests('microsoft', inboxId);
    const { data: googleAccessRequests = [] } = useMailboxAccessRequests('google', inboxId);
    const accessRequests = useMemo(
        () => [...microsoftAccessRequests, ...googleAccessRequests],
        [microsoftAccessRequests, googleAccessRequests]
    );
    const requestMailboxAccess = useRequestMailboxAccess();
    const resendMailboxAccess = useResendMailboxAccess();
    const revokeMailboxAccess = useRevokeMailboxAccess();
    const cancelMailboxAccess = useCancelMailboxAccess();

    const updateInbox = useUpdateInbox();
    const attachMailbox = useAttachMailbox(inboxId);
    const detachMailbox = useDetachMailbox(inboxId);

    const [editorOpen, setEditorOpen] = useState(true);

    // The people picker for member selection and access grants.
    //
    // This used to be derived from the inbox's MEMBERS, because
    // /admin/inbox/employees is require_inbox_admin-gated and 403s for a
    // manager. That made this modal disagree with the admin console's in two
    // visible ways: a manager could only grant access to someone already a
    // member, and an existing grant held by a non-member rendered nowhere —
    // so the Access step under-reported who could open the inbox.
    //
    // `GET /inboxes/{id}/employees` is the manager-legal equivalent, gated on
    // a manager grant for THIS inbox and serving the same rows from the same
    // helper as the admin route.
    const { data: employees = [] } = useInboxEmployeesForInbox(inboxId, editorOpen);
    const [savingId, setSavingId] = useState(null);

    /**
     * Handle a save error the way issue #662 specifies: a 403 means the
     * caller's grant was revoked mid-session — close, toast, refetch the
     * permission store, and never render an empty form.
     */
    const handleSaveError = err => {
        const status = err?.response?.status || err?.status;
        const detail = err?.response?.data?.detail;
        const code = typeof detail === 'object' ? detail?.code : detail;
        if (status === 403) {
            setEditorOpen(false);
            onClose?.();
            toast.error('Your access changed');
            return;
        }
        const messages = {
            inbox_requires_a_manager:
                'An inbox must keep at least one manager',
            cannot_drop_own_manager_grant:
                'You cannot remove your own manager access',
            cannot_revoke_admin_grant:
                "You cannot remove an organisation admin's access",
            inbox_name_taken: 'Another inbox already uses that name',
        };
        toast.error(messages[code] || code || 'Could not save the inbox');
    };

    const handleSave = async patch => {
        try {
            await updateInbox.mutateAsync({ inboxId, ...patch });
        } catch (err) {
            handleSaveError(err);
            throw err; // keep the editor open on rule failures
        }
    };

    const handleAttach = async connectionId => {
        setSavingId(connectionId);
        try {
            await attachMailbox.mutateAsync({ connectionId });
            toast.success('Mailbox attached');
        } catch (err) {
            const status = err?.response?.status || err?.status;
            if (status === 403) {
                onClose?.();
                toast.error('Your access changed');
                return;
            }
            const detail = err?.response?.data?.detail;
            toast.error(detail || 'Could not attach the mailbox');
        } finally {
            setSavingId(null);
        }
    };

    const handleDetach = async connectionId => {
        setSavingId(connectionId);
        try {
            await detachMailbox.mutateAsync(connectionId);
            toast.success('Mailbox detached');
        } catch (err) {
            const status = err?.response?.status || err?.status;
            if (status === 403) {
                onClose?.();
                toast.error('Your access changed');
                return;
            }
            const detail = err?.response?.data?.detail;
            toast.error(detail || 'Could not detach the mailbox');
        } finally {
            setSavingId(null);
        }
    };

    if (inboxesLoading) {
        return (
            <div className="p-6 text-sm text-stone-400" data-testid="inbox-settings-loading">
                Loading…
            </div>
        );
    }

    if (!inbox) {
        return (
            <div className="p-6 text-sm text-stone-500" data-testid="inbox-settings-missing">
                This inbox does not exist, or your access to it was removed.
            </div>
        );
    }

    return (
        <div className="space-y-4" data-testid="inbox-settings-page">
            <div>
                <h2 className="text-lg font-semibold text-stone-900">
                    {inbox.name}
                </h2>
                <p className="text-xs text-stone-400 mt-0.5">
                    Members and mailboxes for this inbox. Organisation-wide
                    settings are managed by admins in the admin console.
                </p>
            </div>

            <div>
                <h3 className="text-xs font-medium text-stone-500 uppercase tracking-wide">
                    Members &amp; access
                </h3>
                <p className="text-xs text-stone-400 mt-0.5 mb-2">
                    Who can open this inbox, and what they can do in it.
                </p>
                <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setEditorOpen(true)}
                    data-testid="inbox-settings-edit-members"
                >
                    Edit members &amp; access
                </Button>
            </div>

            <div>
                <h3 className="text-xs font-medium text-stone-500 uppercase tracking-wide">
                    Mailboxes
                </h3>
                <p className="text-xs text-stone-400 mt-0.5 mb-2">
                    Shared mailboxes pooled into this inbox.
                </p>
                {connections.length === 0 ? (
                    <p className="text-xs text-stone-400">
                        No mailboxes attached yet.
                    </p>
                ) : (
                    <div className="border border-stone-200 rounded-lg divide-y divide-stone-100 max-w-xl">
                        {connections.map(conn => (
                            <div
                                key={conn.connection_id}
                                className="flex items-center gap-3 px-3 py-2"
                                data-testid={`inbox-settings-mailbox-${conn.connection_id}`}
                            >
                                <span className="flex-1 min-w-0">
                                    <span className="block text-sm text-stone-800 truncate">
                                        {conn.email}
                                    </span>
                                    <span className="block text-xs text-stone-400 capitalize">
                                        {conn.provider}
                                    </span>
                                </span>
                                <span
                                    className={`text-xs px-2 py-0.5 rounded-full ${
                                        conn.status === 'active'
                                            ? 'bg-green-100 text-green-800'
                                            : 'bg-stone-100 text-stone-600'
                                    }`}
                                >
                                    {conn.status}
                                </span>
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    disabled={savingId === conn.connection_id}
                                    onClick={() => handleDetach(conn.connection_id)}
                                    data-testid={`inbox-settings-detach-${conn.connection_id}`}
                                >
                                    Detach
                                </Button>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <InboxEditorModal
                open={editorOpen && Boolean(inbox)}
                onOpenChange={open => {
                    setEditorOpen(open);
                    if (!open) onClose?.();
                }}
                inbox={inbox}
                employees={employees}
                onSave={handleSave}
                isSaving={updateInbox.isPending}
                mailboxTokens={mailboxTokens}
                accessRequests={accessRequests}
                mailboxConnections={connections}
                onRequestAccess={async (mail, provider) => {
                    try {
                        await requestMailboxAccess.mutateAsync({
                            mailboxEmail: mail,
                            inboxId,
                            ...(provider ? { provider } : {}),
                        });
                        toast.success(`Access request sent to ${mail}`);
                    } catch (err) {
                        toast.error(err?.response?.data?.detail || 'Could not send access request');
                    }
                }}
                onResend={isAdmin ? async mail => {
                    try {
                        await resendMailboxAccess.mutateAsync({
                            mail,
                            provider: providerForMailbox(mail, { mailboxTokens, accessRequests }),
                        });
                        toast.success(`Access request resent to ${mail}`);
                    } catch (err) {
                        toast.error(err?.response?.data?.detail || 'Could not resend access request');
                    }
                } : undefined}
                onRevoke={isAdmin ? async mail => {
                    try {
                        await revokeMailboxAccess.mutateAsync(mail);
                        toast.success(`Access revoked for ${mail}`);
                    } catch (err) {
                        toast.error(err?.response?.data?.detail || 'Could not revoke access');
                    }
                } : undefined}
                onCancel={isAdmin ? async mail => {
                    try {
                        await cancelMailboxAccess.mutateAsync({
                            mail,
                            provider: providerForMailbox(mail, { mailboxTokens, accessRequests }),
                        });
                        toast.success(`Access request cancelled for ${mail}`);
                    } catch (err) {
                        toast.error(err?.response?.data?.detail || 'Could not cancel access request');
                    }
                } : undefined}
                isRequesting={
                    requestMailboxAccess.isPending ||
                    resendMailboxAccess.isPending ||
                    revokeMailboxAccess.isPending ||
                    cancelMailboxAccess.isPending
                }
                isCancelling={cancelMailboxAccess.isPending}
                onAutoSave={updatedDraft => handleSave(updatedDraft)}
            />
        </div>
    );
}
